import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';

export interface VehicleSectionNavItem {
  id: string;
  label: string;
}

export interface VehicleOwnerToolAction {
  id: string;
  label: string;
  onClick: () => void;
}

interface VehicleSectionNavProps {
  items: VehicleSectionNavItem[];
  ownerTools?: VehicleOwnerToolAction[];
  /** Optional build-profile accent hex for active states */
  accentColor?: string | null;
  className?: string;
  children: React.ReactNode;
}

/** Sticky header on the build page is h-14; spy activates slightly below it. */
const SCROLL_SPY_OFFSET_PX = 96;

/**
 * Ops-style sticky sidebar for the public/owner build page.
 * Desktop: left rail sticks while the build scrolls.
 * Active item tracks scroll depth via section tops vs. the header offset.
 */
export const VehicleSectionNav: React.FC<VehicleSectionNavProps> = ({
  items,
  ownerTools = [],
  accentColor = null,
  className = '',
  children,
}) => {
  const reactId = useId();
  const navId = `build-sections-${reactId.replace(/:/g, '')}`;
  const visible = useMemo(() => items.filter((item) => item.id && item.label), [items]);
  const [activeId, setActiveId] = useState<string>(visible[0]?.id || '');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!visible.some((item) => item.id === activeId) && visible[0]) {
      setActiveId(visible[0].id);
    }
  }, [activeId, visible]);

  const resolveActiveSection = useCallback(() => {
    if (visible.length === 0) return;
    let current = visible[0].id;
    for (const item of visible) {
      const el = document.getElementById(item.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= SCROLL_SPY_OFFSET_PX) {
        current = item.id;
      }
    }
    setActiveId((prev) => (prev === current ? prev : current));
  }, [visible]);

  useEffect(() => {
    if (visible.length === 0) return undefined;

    let frame = 0;
    const onScrollOrResize = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resolveActiveSection);
    };

    // Sections mount with the page content; retry once after paint so late IDs are found.
    resolveActiveSection();
    const settle = window.setTimeout(resolveActiveSection, 120);

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.clearTimeout(settle);
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, resolveActiveSection]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveId(id);
    setMobileOpen(false);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
  }, []);

  const showNav = visible.length >= 2 || ownerTools.length > 0;
  const activeIndex = Math.max(
    0,
    visible.findIndex((item) => item.id === activeId)
  );
  const progress =
    visible.length <= 1 ? 1 : (activeIndex + 1) / visible.length;

  const activeStyle = accentColor
    ? {
        backgroundColor: `${accentColor}1a`,
        borderColor: `${accentColor}55`,
        color: accentColor,
      }
    : undefined;

  const panel = (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06] shrink-0">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold tracking-tight text-white">On this build</h2>
          <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
            {visible.find((i) => i.id === activeId)?.label
              ? `Viewing ${visible.find((i) => i.id === activeId)?.label}`
              : 'Jump to a section'}
          </p>
        </div>
        <button
          type="button"
          className="lg:hidden p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-white cursor-pointer rounded-lg"
          aria-label="Close sections"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="w-5 h-5" aria-hidden />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-5">
        {visible.length > 0 ? (
          <div>
            <div className="flex items-center justify-between px-2 mb-1.5">
              <p className="py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Sections
              </p>
              <p className="text-[11px] tabular-nums text-gray-600" aria-hidden>
                {activeIndex + 1}/{visible.length}
              </p>
            </div>
            {/* Scroll-depth rail: fills as the user moves through sections. */}
            <div
              className="relative mb-2 mx-2 h-1 rounded-full bg-white/[0.06] overflow-hidden"
              aria-hidden
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-orange-400/80 transition-[width] duration-300 ease-out"
                style={{
                  width: `${Math.round(progress * 100)}%`,
                  ...(accentColor ? { backgroundColor: accentColor } : {}),
                }}
              />
            </div>
            <ul className="mt-1 space-y-0.5 list-none">
              {visible.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(item.id);
                      }}
                      className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 min-h-[44px] cursor-pointer border ${
                        active
                          ? accentColor
                            ? 'border'
                            : 'bg-white/[0.06] text-white border-white/20'
                          : 'text-gray-300 border-transparent hover:bg-white/[0.04] hover:text-white'
                      }`}
                      style={active ? activeStyle : undefined}
                      aria-current={active ? 'location' : undefined}
                    >
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? '' : 'bg-white/20'}`}
                        style={
                          active
                            ? { backgroundColor: accentColor || '#e5e7eb' }
                            : undefined
                        }
                        aria-hidden
                      />
                      <span className="truncate">{item.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {ownerTools.length > 0 ? (
          <div>
            <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Owner tools
            </p>
            <p className="px-2 text-[11px] text-gray-600 mb-1.5">Only you see these</p>
            <ul className="mt-1 space-y-0.5 list-none">
              {ownerTools.map((tool) => (
                <li key={tool.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      tool.onClick();
                    }}
                    className="w-full text-left flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-transparent hover:bg-white/[0.04] hover:text-white transition-colors duration-200 min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                  >
                    {tool.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (!showNav) {
    return <div className="w-full">{children}</div>;
  }

  const mobileDrawer =
    mobileOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[80] bg-black/55 lg:hidden cursor-pointer"
              aria-label="Close sections overlay"
              onClick={() => setMobileOpen(false)}
            />
            <aside
              id={navId}
              className="fixed inset-y-0 left-0 z-[90] flex flex-col w-[min(20rem,88vw)] max-w-full border-r border-white/[0.06] bg-[#0c0c0c] overflow-hidden shadow-2xl shadow-black/50 lg:hidden"
              aria-label="On this build"
              role="dialog"
              aria-modal="true"
            >
              {panel}
            </aside>
          </>,
          document.body
        )
      : null;

  return (
    <div className={`w-full ${className}`.trim()}>
      <div className="lg:hidden mb-4 sticky top-14 z-30 -mx-1 px-1 py-2 bg-[#0a0a0a]/90 backdrop-blur-md">
        <Button
          type="button"
          variant="secondary"
          className="w-full sm:w-auto min-h-[44px]"
          onClick={() => setMobileOpen(true)}
          aria-expanded={mobileOpen}
          aria-controls={navId}
        >
          <Bars3Icon className="w-5 h-5" aria-hidden />
          On this build
          {activeId ? (
            <span className="ml-1 text-xs text-gray-400 font-normal truncate max-w-[9rem]">
              · {visible.find((i) => i.id === activeId)?.label || 'Sections'}
            </span>
          ) : null}
        </Button>
      </div>

      <div className="flex gap-0 lg:gap-8 items-start">
        {/* Desktop rail stays in-flow. Mobile drawer is portaled so page
            overflow-x-clip cannot trap position:fixed off-screen. */}
        <aside
          className="hidden lg:flex flex-col sticky top-20 z-20 w-60 xl:w-64 shrink-0 self-start max-h-[calc(100vh-6rem)] border border-white/[0.06] rounded-xl bg-[#0c0c0c] overflow-hidden"
          aria-label="On this build"
        >
          {panel}
        </aside>

        {mobileDrawer}

        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

export default VehicleSectionNav;
